import { observable } from 'mobx';
import { QueryResultRow, QueryResults } from '@kentik/ui-shared/query/query.types';
import { Collection, CollectionOptions, ModelKey } from 'core/model/Collection';
import UdeQueryResultModel from './UdeQueryResultModel';
import type QueryModel from './QueryModel';

export default class UdeQueryResultsCollection extends Collection<UdeQueryResultModel> {
  @observable.ref timestamps: number[] = [];

  constructor(data: QueryResults = { timestamps: [], rows: [] }, options?: CollectionOptions<UdeQueryResultModel>) {
    const { timestamps, rows } = data;
    super(rows, options);
    this.timestamps = timestamps;
  }

  get queryModel() {
    if (!this.parent) {
      throw new Error('UdeQueryResultsCollection must have a parent QueryModel');
    }
    return this.parent as unknown as QueryModel;
  }

  get model() {
    return UdeQueryResultModel;
  }

  get url() {
    return '';
  }

  get defaultSortState() {
    return {
      direction: 'desc' as const
    };
  }

  get baseSort() {
    const sort = this.queryModel.query.sort?.[0] || { rollup: this.queryModel.primaryRollupKey, order: 'desc' };
    return { field: `rollups.${sort.rollup}` as ModelKey<UdeQueryResultModel>, direction: sort.order };
  }

  get rows() {
    return this.original || this.models;
  }

  /** Update the collection with new data without resetting state (ex. live update) */
  update(data: QueryResults) {
    const { timestamps, rows } = data;
    this.timestamps = timestamps;
    this.set(rows);
  }

  /** Replace the collection with new data and reset state (ex. new query) */
  replace(data: QueryResults) {
    const { timestamps, rows } = data;
    this.reset({ hard: true });
    this.timestamps = timestamps;
    this.set(rows);
  }

  reset(options?: { hard?: boolean }) {
    super.reset(options);
    this.timestamps = [];
  }

  // @ts-expect-error Collection serialize doesn't allow returning additional data
  serialize(data?: QueryResultRow[]): QueryResults {
    return {
      timestamps: this.timestamps,
      rows: data || this.models.map((model) => model.serialize())
    };
  }
}
